import React, { useState, useEffect, useCallback } from "react";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { useToast } from "components/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  fetchAllOrganizations,
  addUsernameToCityAccess,
  removeUsernameFromCityAccess,
  fetchCityAccessForOrganization,
} from "api/masteraccessapi";
import { checkUserExists } from "api/adminapi";

const MasterAccessInfoForm = () => {
  const { toast } = useToast();

  // Form state
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [account, setAccount] = useState("");
  const [cityAccess, setCityAccess] = useState([]);
  const [selectedAccountToRemove, setSelectedAccountToRemove] = useState("");

  // Loading states
  const [loading, setLoading] = useState(false);
  const [removingAccount, setRemovingAccount] = useState(false);
  const [fetchingOrgs, setFetchingOrgs] = useState(true);
  const [fetchingCityAccess, setFetchingCityAccess] = useState(false);

  // UI state
  const [orgSelectOpen, setOrgSelectOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Helper functions
  const showErrorToast = useCallback(
    (title, description) => {
      toast({
        title,
        description,
        variant: "destructive",
      });
    },
    [toast]
  );

  const showSuccessToast = useCallback(
    (title, description) => {
      toast({
        title,
        description,
      });
    },
    [toast]
  );

  const refreshCityAccess = useCallback(async () => {
    if (!selectedOrganization) return;

    try {
      const result = await fetchCityAccessForOrganization(selectedOrganization);
      if (!result.error) {
        setCityAccess(result.data || []);
      }
    } catch (error) {
      console.error("Error refreshing city access:", error);
    }
  }, [selectedOrganization]);

  // Check if user exists in Supabase Auth using Edge Function
  const checkUserExistsWrapper = useCallback(async (username) => {
    try {
      return await checkUserExists(username);
    } catch (error) {
      console.error("Error checking user existence:", error);
      return { error: error.message || "Unable to verify user existence" };
    }
  }, []);

  // Fetch all organizations on component mount
  useEffect(() => {
    let cancelled = false;

    const loadOrganizations = async () => {
      setFetchingOrgs(true);
      try {
        const result = await fetchAllOrganizations();
        if (cancelled) return;

        if (result.error) {
          showErrorToast(
            "Error",
            `Failed to load organizations: ${result.error}`
          );
        } else {
          setOrganizations(result.data || []);
          setIsReady(true);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching organizations:", error);
        showErrorToast("Error", "Failed to load organizations");
      } finally {
        if (!cancelled) {
          setFetchingOrgs(false);
        }
      }
    };

    loadOrganizations();

    return () => {
      cancelled = true;
    };
  }, [showErrorToast]);

  // Fetch city_access when organization changes
  useEffect(() => {
    let cancelled = false;

    const loadCityAccess = async () => {
      if (!selectedOrganization) {
        if (!cancelled) {
          setCityAccess([]);
        }
        return;
      }

      setFetchingCityAccess(true);
      try {
        const result = await fetchCityAccessForOrganization(
          selectedOrganization
        );
        if (cancelled) return;

        if (result.error) {
          showErrorToast(
            "Error",
            `Failed to load city access: ${result.error}`
          );
          setCityAccess([]);
        } else {
          const cityAccessData = Array.isArray(result.data) ? result.data : [];
          setCityAccess(cityAccessData);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching city access:", error);
        showErrorToast("Error", "Failed to load city access");
        setCityAccess([]);
      } finally {
        if (!cancelled) {
          setFetchingCityAccess(false);
        }
      }
    };

    loadCityAccess();

    return () => {
      cancelled = true;
    };
  }, [selectedOrganization, showErrorToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validation
    if (!selectedOrganization) {
      showErrorToast("Validation Error", "Please select an organization");
      return;
    }

    const trimmedAccount = account.trim();
    if (!trimmedAccount) {
      showErrorToast("Validation Error", "Please enter a account");
      return;
    }

    setLoading(true);
    try {
      // Validate if user exists in Supabase Auth
      const userCheck = await checkUserExistsWrapper(trimmedAccount);

      if (userCheck.error) {
        showErrorToast(
          "Error",
          `Unable to verify user account: ${userCheck.error}`
        );
        setLoading(false);
        return;
      }

      if (!userCheck.exists) {
        // User doesn't exist
        showErrorToast(
          `Account "${trimmedAccount}" does not exist in the database. Please ensure the user account has been created first.`
        );
        setLoading(false);
        return;
      }

      // User exists - proceed to add to master access
      const result = await addUsernameToCityAccess(
        selectedOrganization,
        trimmedAccount
      );

      if (result.error) {
        showErrorToast("Error", result.error);
      } else {
        showSuccessToast(
          "Success",
          `Account "${trimmedAccount}" has been added to the master account for "${selectedOrganization}"`
        );
        setAccount("");
        await refreshCityAccess();
      }
    } catch (error) {
      console.error("Error adding account to master account:", error);
      showErrorToast("Error", "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAccountClick = () => {
    // Validation
    if (!selectedOrganization) {
      showErrorToast("Validation Error", "Please select an organization");
      return;
    }
    if (!selectedAccountToRemove) {
      showErrorToast("Validation Error", "Please select a account to remove");
      return;
    }
    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmRemove = async () => {
    setShowConfirmDialog(false);
    setRemovingAccount(true);
    try {
      const result = await removeUsernameFromCityAccess(
        selectedOrganization,
        selectedAccountToRemove
      );
      if (result.error) {
        showErrorToast("Error", result.error);
      } else {
        showSuccessToast(
          "Success",
          `Account "${selectedAccountToRemove}" has been removed from master account for "${selectedOrganization}"`
        );
        setSelectedAccountToRemove("");
        await refreshCityAccess();
      }
    } catch (error) {
      console.error("Error removing account from master account:", error);
      showErrorToast("Error", "An unexpected error occurred");
    } finally {
      setRemovingAccount(false);
    }
  };

  const handleInputChange = useCallback((e) => {
    setAccount(e.target.value);
  }, []);

  const handleOrganizationChange = useCallback((value) => {
    setSelectedOrganization(value);
    setSelectedAccountToRemove(""); // Reset selection when org changes
  }, []);

  // Render organization select
  const renderOrganizationSelect = () => {
    if (!isReady) {
      return (
        <div className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center">
          <span className="text-muted-foreground">
            {fetchingOrgs
              ? "Loading organizations..."
              : "Select an organization"}
          </span>
        </div>
      );
    }

    return (
      <Select
        value={selectedOrganization}
        onValueChange={handleOrganizationChange}
        open={orgSelectOpen}
        onOpenChange={setOrgSelectOpen}
        disabled={fetchingOrgs || loading}>
        <SelectTrigger id="organization">
          <SelectValue
            placeholder={
              fetchingOrgs
                ? "Loading organizations..."
                : "Select an organization"
            }
          />
        </SelectTrigger>
        <SelectContent>
          {organizations.map((org) => (
            <SelectItem
              key={org.id}
              value={org.organization_name}>
              {org.organization_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  // Render username select for removal
  const renderUsernameSelect = () => {
    if (fetchingCityAccess) {
      return (
        <p className="text-sm text-muted-foreground">Loading accounts...</p>
      );
    }

    if (cityAccess.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          No accounts found for this organization
        </p>
      );
    }

    const validUsers = cityAccess.filter((user) => user != null && user !== "");

    return (
      <Select
        value={selectedAccountToRemove}
        onValueChange={setSelectedAccountToRemove}
        disabled={removingAccount || loading || fetchingCityAccess}>
        <SelectTrigger id="username-to-remove">
          <SelectValue placeholder="Select a username to remove" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {validUsers.length > 0 ? (
            validUsers.map((user, index) => {
              const userValue = String(user);
              return (
                <SelectItem
                  key={`user-${index}-${userValue}`}
                  value={userValue}>
                  {userValue}
                </SelectItem>
              );
            })
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No accounts available
            </div>
          )}
        </SelectContent>
      </Select>
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Add Account to Master Access</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="organization">Organization Name:</Label>
              {renderOrganizationSelect()}
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">Account:</Label>
              <input
                id="username"
                type="text"
                name="username"
                value={account}
                placeholder="Enter account (e.g., City_of_X)"
                onChange={handleInputChange}
                disabled={loading}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={account.trim() === "" || loading || fetchingOrgs}>
            {account.trim() === ""
              ? "Please enter an account"
              : loading
              ? "Adding..."
              : "Add Account"}
          </Button>
        </div>

        {/* Remove Username Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">
            Remove Account from Master Access
          </h3>
          {!selectedOrganization ? (
            <p className="text-sm text-muted-foreground">
              Please select an organization to view and remove accounts
            </p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username-to-remove">
                  Select Account to Remove:
                </Label>
                {renderUsernameSelect()}
              </div>
              {selectedAccountToRemove && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={handleRemoveAccountClick}
                    disabled={removingAccount || loading || fetchingCityAccess}>
                    {removingAccount ? "Removing..." : "Remove Account"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Removal</DialogTitle>
            <DialogDescription>
              Are you sure you want to do this?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => setShowConfirmDialog(false)}
              disabled={removingAccount}>
              No
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRemove}
              disabled={removingAccount}>
              {removingAccount ? "Removing..." : "Yes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default MasterAccessInfoForm;
